<template>
  <section :class="embedded ? 'py-0' : 'bg-[#fcf5ec] py-12'">
    <div :class="embedded ? 'p-0  mx-auto' : 'bg-[#EEDCC5] p-6 lg:p-12 mx-auto'">
      <h2 class="text-[38px] font-black italic uppercase text-[#111] mb-6">{{ t('productCreate.title') }}</h2>

      <div v-if="!canManage" class="text-[#842029] bg-[#f8d7da] border border-[#f5c2c7] p-3 text-[14px]">
        {{ t('productCreate.notAuthorizedNotice') || 'Je hebt geen rechten om materialen toe te voegen.' }}
      </div>

      <div v-else class="bg-[#F6EBDC] p-5">
        <form @submit.prevent="submit" class="grid grid-cols-1 gap-6 max-w-[1060px]">
          <!-- Basis -->
          <div class="">
            <div class="flex items-center gap-3 mb-4">
              <div class="w-10 h-10 bg-black text-white flex-shrink-0 grid place-items-center font-extrabold">1</div>
              <h3 class="text-[20px] font-black italic uppercase m-0">{{ t('productCreate.sections.base') }}</h3>
            </div>
            <div class="space-y-4">
              <div class="flex items-center justify-between gap-2">
                <label class="text-[14px] text-[#333] font-bold">{{ t('productCreate.labels.title') }} *</label>
                <MmInfoTip :text="t('productCreate.tips.title')" />
              </div>
              <input v-model="model.title" type="text" required :placeholder="t('productCreate.placeholders.title')"
                     class="h-12 px-3 border bg-white w-full border-[#d8d1c6] box-border" />
              <!-- Samenvatting vervalt: de tegel toont automatisch de eerste ~140 tekens uit de beschrijving -->
              <div class="flex items-center justify-between gap-2">
                <label class="text-[14px] text-[#333] font-bold">{{ t('productCreate.labels.description') }} *</label>
                <MmInfoTip :text="t('productCreate.tips.description')" />
              </div>
              <textarea v-model="model.content" rows="6" required :placeholder="t('productCreate.placeholders.description')"
                        class="px-3 py-2 border bg-white w-full border-[#d8d1c6] box-border"></textarea>
            </div>
          </div>

          <!-- Eigenschappen -->
          <div class="">
            <div class="flex items-center gap-3 mb-4">
              <div class="w-10 h-10 bg-black text-white flex-shrink-0 grid place-items-center font-extrabold">2</div>
              <h3 class="text-[20px] font-black italic uppercase m-0">{{ t('productCreate.sections.properties') }}</h3>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
              <div>
                <div class="flex items-center justify-between mb-1 gap-2">
                  <label class="text-[14px] text-[#333] font-bold">{{ t('materials.type') || 'Type' }} *</label>
                </div>
              <div class="relative">
                <select v-model="model.mm_type" required
                        class="h-12 px-3 pr-8 border bg-white w-full border-[#d8d1c6] box-border appearance-none">
                  <option value="aangeboden">{{ t('materials.offered') || 'Aangeboden' }}</option>
                  <option value="gevraagd">{{ t('materials.requested') || 'Gevraagd' }}</option>
                </select>
                <svg class="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" fill="none"
                     stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </div>
              </div>
              <div v-if="model.mm_type !== 'gevraagd'">
                <div class="flex items-center justify-between mb-1 gap-2">
                  <label class="text-[14px] text-[#333] font-bold">{{ t('productCreate.labels.valueEUR') }} *</label>
                  <MmInfoTip :text="t('productCreate.tips.valueEUR')" />
                </div>
                <input v-model.number="model.waarde" type="number" step="0.01" min="0" required
                       :placeholder="t('productCreate.placeholders.valueEUR')"
                       class="h-12 px-3 border bg-white w-full border-[#d8d1c6] box-border" />
              </div>
              <div>
                <div class="flex items-center justify-between mb-1 gap-2">
                  <label class="text-[14px] text-[#333] font-bold">{{ t('productCreate.labels.condition') }} *</label>
                  <MmInfoTip :text="t('productCreate.tips.status')" />
                </div>
                <div class="relative">
                  <select v-model="model.status" required
                          class="h-12 px-3 pr-8 border bg-white w-full border-[#d8d1c6] box-border appearance-none">
                    <option value="">{{ t('productCreate.placeholders.condition') }}</option>
                    <option value="nieuw">{{ t('productCreate.conditionOptions.nieuw') }}</option>
                    <option value="gebruikt">{{ t('productCreate.conditionOptions.gebruikt') }}</option>
                    <option value="defect">{{ t('productCreate.conditionOptions.defect') }}</option>
                  </select>
                  <svg class="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" fill="none"
                       stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
              </div>
              <div>
                <div class="flex items-center justify-between mb-1 gap-2">
                  <label class="text-[14px] text-[#333] font-bold">{{ t('productCreate.labels.dimensions') }}</label>
                  <MmInfoTip :text="t('productCreate.tips.dimensions')" />
                </div>
                <input v-model="model.afmetingen" type="text" :placeholder="t('productCreate.placeholders.dimensions')"
                       class="h-12 px-3 border bg-white w-full border-[#d8d1c6] box-border" />
              </div>
              <div>
                <div class="flex items-center justify-between mb-1 gap-2">
                  <label class="text-[14px] text-[#333] font-bold">{{ t('productCreate.labels.weight') }}</label>
                  <MmInfoTip :text="t('productCreate.tips.weight')" />
                </div>
                <input v-model="model.gewicht" type="text" :placeholder="t('productCreate.placeholders.weight')"
                       class="h-12 px-3 border bg-white w-full border-[#d8d1c6] box-border" />
              </div>
              <div>
                <div class="flex items-center justify-between mb-1 gap-2">
                  <label class="text-[14px] text-[#333] font-bold">{{ t('productCreate.labels.pickupLocation') }} *</label>
                  <MmInfoTip :text="t('productCreate.tips.pickupLocation')" />
                </div>
                <MmLocationAutocomplete
                  v-model="model.locationData"
                  :placeholder="t('productCreate.placeholders.pickupLocation')"
                  :initialText="model.locatie"
                  inputClass="h-12 px-3 border bg-white w-full border-[#d8d1c6] box-border"
                  :required="true"
                  @select="onLocationSelect"
                  @clear="onLocationClear"
                />
              </div>
            </div>
          </div>

          <!-- Categorieën -->
          <div class="overflow-hidden">
            <div class="flex items-center gap-3 mb-4">
              <div class="w-10 h-10 bg-black text-white flex-shrink-0 grid place-items-center font-extrabold">3</div>
              <h3 class="text-[20px] font-black italic uppercase m-0">{{ t('productCreate.sections.categories') }} *</h3>
            </div>
            <div class="flex flex-wrap gap-2">
              <label v-for="c in categories" :key="c.id" class="inline-flex items-center gap-2 text-[13px] px-2 py-1 bg-white border border-[#d8d1c6] cursor-pointer hover:border-black transition-colors">
                <input type="checkbox" :value="c.id" v-model="model.categories" class="w-4 h-4 accent-black">
                <span>{{ c.name }}</span>
              </label>
            </div>
            <div v-if="catError" class="text-[13px] text-[#b91c1c] mt-2">{{ t('productCreate.validation.selectOneCategory') }}</div>
          </div>

          <!-- Afbeeldingen -->
          <div class="overflow-hidden">
            <div class="flex items-center gap-3 mb-4">
              <div class="w-10 h-10 bg-black text-white flex-shrink-0 grid place-items-center font-extrabold">4</div>
              <h3 class="text-[20px] font-black italic uppercase m-0">{{ t('productCreate.sections.image') }} *</h3>
            </div>
            
            <div class="bg-white border-2 border-dashed border-[#d8d1c6] p-4 sm:p-6 space-y-4 hover:border-black transition-colors overflow-hidden" :class="{'border-[#b91c1c]': imgError}">
              <div class="flex items-center justify-between gap-2">
                <label for="material-files" class="inline-flex items-center px-4 py-2 bg-black text-white text-[12px] sm:text-[14px] font-bold uppercase italic cursor-pointer hover:bg-gray-800 transition-colors shrink-0">
                  <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path></svg>
                  {{ t('productCreate.image.galleryTitle') || 'Afbeeldingen selecteren' }}
                </label>
                <MmInfoTip :text="t('productCreate.tips.gallery') || 'Voeg foto\'s toe om een goed beeld te geven van het materiaal. De eerste foto wordt de hoofdafbeelding.'" />
              </div>
              
              <input type="file" id="material-files" multiple accept="image/jpeg,image/png,image/gif,image/webp" @change="onFiles" class="hidden" />

              <div v-if="imgError" class="text-[13px] text-[#b91c1c]">{{ t('productCreate.validation.imageRequired') }}</div>

              <div v-if="previews.length" class="text-[13px] font-semibold text-black">
                {{ previews.length }} {{ previews.length === 1 ? 'afbeelding' : 'afbeeldingen' }} geselecteerd:
              </div>

              <div v-if="previews.length" class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-4 mt-4">
                <div v-for="(p, idx) in previews" :key="idx" class="relative group aspect-square bg-[#f7f3ec] border border-[#e6ded2] overflow-hidden" :class="{'border-black ring-1 ring-black': idx === 0}">
                  <img :src="p.url" class="object-cover w-full h-full cursor-zoom-in" @click="zoomImage = p.url" />
                  
                  <div class="absolute top-0 left-0 flex flex-col gap-1">
                    <div v-if="idx === 0" class="bg-black text-white text-[9px] px-1.5 py-0.5 font-bold uppercase tracking-wider">
                      Hoofdfoto
                    </div>
                    <div class="bg-blue-600 text-white text-[9px] px-1.5 py-0.5 font-bold uppercase tracking-wider">
                      Preview
                    </div>
                  </div>

                  <button type="button" @click="removeFile(idx)" class="absolute top-1 right-1 bg-red-600 text-white p-1 shadow-md hover:bg-red-700" title="Verwijderen">
                    <svg class="w-3 h-3 sm:w-4 sm:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                  </button>
                  <div class="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] px-1 truncate py-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    {{ p.name }}
                  </div>
                </div>
              </div>
              <p class="text-[12px] text-[#7a7366] italic leading-relaxed">{{ t('productCreate.image.recommended') }}</p>
            </div>
          </div>

          <!-- Acties -->
          <div class="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <button type="button" disabled v-if="submitting"
                    class="w-full sm:w-auto px-5 py-3 bg-black text-white uppercase italic font-semibold disabled:opacity-60">{{ t('buttons.saving') }}</button>
            <button v-else type="submit" class="w-full sm:w-auto px-5 py-3 bg-black text-white uppercase italic font-semibold hover:bg-[#222]">{{ t('productManage.buttons.saveAndUpload') || 'Definitief opslaan en uploaden' }}</button>

            <div v-if="error" class="w-full sm:w-auto text-[#842029] bg-[#f8d7da] border border-[#f5c2c7] px-3 py-2 text-[14px]">{{ error }}</div>
            <div v-if="successId" class="w-full sm:w-auto text-[#0f5132] bg-[#d1e7dd] border border-[#badbcc] px-3 py-2 text-[14px]">{{ t('productCreate.messages.createdPrefix') }} {{ successId }}</div>
          </div>
        </form>
      </div>
    </div>
  </section>

  <!-- Zoom modal -->
  <MmModal v-model="zoomImage" :title="t('productManage.labels.image.zoomTitle') || 'Afbeelding vergroten'">
    <div class="flex items-center justify-center bg-black/5 p-2 rounded overflow-hidden min-h-[300px]">
      <img v-if="zoomImage" :src="zoomImage" alt="Zoom" class="max-w-full max-h-[70vh] object-contain shadow-lg" />
    </div>
    <template #footer>
      <button type="button" class="mm-modal__button" @click="zoomImage = null">{{ t('buttons.close') }}</button>
    </template>
  </MmModal>
</template>

<script>
import WpApiClient from '../lib/wp-api.js'
import MmInfoTip from './MmInfoTip.vue'
import MmModal from './MmModal.vue'
import MmLocationAutocomplete from './MmLocationAutocomplete.vue'
import { t } from '../i18n'

export default {
  name: 'MmProductCreate',
  components: { MmInfoTip, MmModal, MmLocationAutocomplete },
  props: { embedded: { type: Boolean, default: false } },
  data(){
    return {
      // Expose translator to template context so {{ t('...') }} works in Options API
      t,
      api: null,
      user: null,
      loading: true,
      submitting: false,
      categories: [],
      model: {
        title: '',
        content: '',
        waarde: '',
        afmetingen: '',
        gewicht: '',
        locatie: '',
        locationData: null,
        status: '',
        mm_type: 'aangeboden',
        categories: [],
        files: [],
      },
      previews: [],
      zoomImage: null,
      error: '',
      successId: null,
    }
  },
  computed: {
    canManage(){
      const rt = ((this.user?.roleType)||'').toLowerCase()
      return rt === 'bedrijf' || rt === 'school'
    },
    isBedrijf(){ return ((this.user?.roleType)||'').toLowerCase()==='bedrijf' },
    catError(){ return this.submitting && !this.model.categories.length },
    imgError(){ return this.submitting && !this.model.files.length }
  },
  methods: {
    async init(){
      this.api = new WpApiClient()
      try {
        let res = await this.api.getCurrentUser({ nonce: window?.materiaalAjax?.nonce })
        
        // Automatische retry bij nonce error
        if (res && !res.ok && (res.error === 'invalid_nonce' || (res.error && res.error.includes('nonce')))) {
          const newNonce = await this.api.refreshNonce(false);
          if (newNonce) {
            res = await this.api.getCurrentUser({ nonce: newNonce });
          }
        }

        this.user = res?.user || null
      } catch(_){ this.user = null }
      try {
        this.categories = await this.api.getCategories({ taxonomy: 'materiaal_categorie', perPage: 100 })
      } catch(_){ this.categories = [] }
      this.loading = false
    },
    onFiles(e){
      const files = Array.from(e.target.files || [])
      if (!files.length) return

      files.forEach(f => {
        this.model.files.push(f)
        try {
          this.previews.push({
            url: URL.createObjectURL(f),
            name: f.name
          })
        } catch(err) {
          console.error("Preview error:", err)
        }
      })

      // Force reactivity
      this.model.files = [...this.model.files]
      this.previews = [...this.previews]

      // Reset input zodat dezelfde bestanden opnieuw gekozen kunnen worden indien gewenst
      e.target.value = ''
    },
    removeFile(idx){
      const p = this.previews[idx]
      if (p && p.url) {
        try { URL.revokeObjectURL(p.url) } catch(_){}
      }
      this.model.files.splice(idx, 1)
      this.previews.splice(idx, 1)
    },
    onLocationSelect(item) {
      this.model.locationData = item
      this.model.locatie = item?.display_name || item?.place_name || ''
    },
    onLocationClear() {
      this.model.locationData = null
      this.model.locatie = ''
    },
    async submit(){
      this.error = ''
      this.successId = null
      if (!this.canManage) { this.error = t('productCreate.validation.noRights'); return }
      if (!this.model.title) { return }
      if (this.model.mm_type !== 'gevraagd' && !this.model.waarde) { return }
      if (!this.model.status) { return }
      if (!this.model.categories.length) { this.error = t('productCreate.validation.selectOneCategory'); return }
      if (!this.model.files.length) { this.error = t('productCreate.validation.imageRequired'); return }
      
      const locatieStr = this.model.locationData?.display_name || this.model.locationData?.place_name || this.model.locatie
      if (!locatieStr) { return }

      this.submitting = true
      try{
        // We mappen het eerste bestand naar imageFile en de rest naar galleryFiles voor de API
        const imageFile = this.model.files.length ? this.model.files[0] : null
        const galleryFiles = this.model.files.length > 1 ? this.model.files.slice(1) : []

        const payload = {
          title: this.model.title,
          content: this.model.content,
          categories: this.model.categories,
          waarde: this.model.waarde,
          afmetingen: this.model.afmetingen,
          gewicht: this.model.gewicht,
          locatie: this.model.locationData?.display_name || this.model.locationData?.place_name || this.model.locatie,
          locationData: this.model.locationData || null,
          status: this.model.status,
          type: this.model.mm_type,
          imageFile: imageFile,
          galleryFiles: galleryFiles,
          nonce: window?.materiaalAjax?.nonce,
        }
        const res = await this.api.createMaterial(payload)
        if (res?.ok) {
          this.successId = res.id
          // reset
          this.model.title = ''
          this.model.content = ''
          this.model.waarde = ''
          this.model.afmetingen = ''
          this.model.gewicht = ''
          this.model.locatie = ''
          this.model.locationData = null
          this.model.status = ''
          this.model.mm_type = 'aangeboden'
          this.model.categories = []
          this.model.files = []
          
          this.previews.forEach(p => {
            if (p.url) try { URL.revokeObjectURL(p.url) } catch(_){}
          })
          this.previews = []
        } else {
          const code = res?.error
          if (code === 'invalid_title_chars') {
            this.error = 'Titel bevat niet-toegestane tekens.'
          } else if (code === 'missing_title') {
            this.error = 'Titel is verplicht.'
          } else if (code === 'missing_location') {
            this.error = 'Locatie is verplicht.'
          } else {
            this.error = code || 'Onbekende fout'
          }
        }
      } catch(e){
        this.error = e?.message || String(e)
      } finally {
        this.submitting = false
      }
    }
  },
  mounted(){ this.init() }
}
</script>

<style scoped>
</style>
